// product/ImageGallery.jsx
// Image carousel with thumbnail strip.
// Supports swipe on mobile via touch events.
// Props: images — array of { image_url, alt_text, is_primary }

import React, { useState, useRef } from "react";
import { ChevronLeft, ChevronRight, Package } from "lucide-react";
import { brand } from "../shared/brand";

export function ImageGallery({ images = [] }) {
  const [active, setActive]   = useState(0);
  const touchStartX           = useRef(null);

  if (images.length === 0) {
    return (
      <div
        className="aspect-square rounded-2xl flex items-center justify-center bg-slate-100"
        style={{ minHeight: 300 }}
      >
        <Package className="size-16 text-slate-300" />
      </div>
    );
  }

  // ── Touch swipe ────────────────────────────────────────────────────────────
  function onTouchStart(e) { touchStartX.current = e.touches[0].clientX; }
  function onTouchEnd(e) {
    if (touchStartX.current === null) return;
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) < 40) return;
    if (delta > 0) setActive(i => Math.min(i + 1, images.length - 1));
    else           setActive(i => Math.max(i - 1, 0));
    touchStartX.current = null;
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Main image */}
      <div
        className="relative aspect-square rounded-2xl overflow-hidden bg-slate-100 select-none"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <img
          key={active}
          src={images[active].image_url}
          alt={images[active].alt_text || "Producto"}
          className="h-full w-full object-cover transition-opacity duration-200"
          loading="eager"
        />

        {/* Prev / Next arrows — hidden on mobile, visible md+ */}
        {images.length > 1 && (
          <>
            <button
              onClick={() => setActive(i => Math.max(i - 1, 0))}
              disabled={active === 0}
              className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 size-8 rounded-full bg-white/80 items-center justify-center shadow disabled:opacity-30 transition-opacity"
            >
              <ChevronLeft className="size-4 text-slate-700" />
            </button>
            <button
              onClick={() => setActive(i => Math.min(i + 1, images.length - 1))}
              disabled={active === images.length - 1}
              className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 size-8 rounded-full bg-white/80 items-center justify-center shadow disabled:opacity-30 transition-opacity"
            >
              <ChevronRight className="size-4 text-slate-700" />
            </button>
          </>
        )}

        {/* Dot indicators — mobile */}
        {images.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 md:hidden">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className="rounded-full transition-all duration-200"
                style={{
                  width:      i === active ? 16 : 6,
                  height:     6,
                  background: i === active ? brand.primary : "rgba(0,0,0,0.25)",
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Thumbnail strip — visible md+ */}
      {images.length > 1 && (
        <div className="hidden md:flex gap-2 overflow-x-auto pb-1">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className="shrink-0 size-16 rounded-xl overflow-hidden border-2 transition-all duration-150"
              style={{ borderColor: i === active ? brand.primary : "transparent" }}
            >
              <img
                src={img.image_url}
                alt={img.alt_text || `Foto ${i + 1}`}
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}